<script lang="ts">
	import Icon from '../Icon.svelte';

	interface Props {
		id?: string | null;
		dataTestId?: string | null;
		iconName?: string | null;
		size?: 'big' | 'normal' | 'small';
		primary?: boolean;
		disabled?: boolean;
		mainColor?: 'primary' | 'secondary' | 'error' | 'warning';
		text?: string | null;
		showCircle?: boolean;
		hoverText?: string | null;
		active?: boolean;
		spamProtectedCall?: (() => Promise<any>) | null;
		wiggle?: boolean;
		onClick?: (e: MouseEvent) => void;
	}

	let {
		id = null,
		dataTestId = null,
		iconName = $bindable(null),
		size = 'normal',
		primary = false,
		disabled = false,
		mainColor = 'primary',
		text = null,
		showCircle = true,
		hoverText = '',
		active = false,
		spamProtectedCall = null,
		wiggle = false,
		onClick = undefined
	}: Props = $props();
	let spamProtected = $state(false);
</script>

<button
	{id}
	data-testid={dataTestId}
	title={hoverText}
	class="p-2 flex justify-center items-center border-2 rounded-full"
	class:borderless={!showCircle}
	class:primary
	class:secondary={!primary}
	class:primary-color={mainColor === 'primary' && !disabled}
	class:secondary-color={mainColor === 'secondary' && !disabled}
	class:error-color={mainColor === 'error' && !disabled}
	class:warning-color={mainColor === 'warning' && !disabled}
	class:active
	class:h-8={size === 'small'}
	class:w-8={size === 'small'}
	class:h-10={size === 'normal'}
	class:w-10={size === 'normal'}
	class:h-12={size === 'big'}
	class:w-12={size === 'big'}
	tabindex="-1"
	{disabled}
	onclick={(e: MouseEvent) => {
		onClick?.(e);
		if (spamProtectedCall) {
			//Prevent the call
			if (spamProtected) return;

			//Set as running
			spamProtected = true;

			//Call the callable
			spamProtectedCall()
				//Add small delay for reactivation
				.then(() => new Promise((resolve) => setTimeout(resolve, 200)))
				.finally(() => {
					//Revert the runnung flag
					spamProtected = false;
				});
		}
	}}
>
	{#if text != null}
		<p class="font-bold">{text.toUpperCase()}</p>
	{:else}
		<Icon name={iconName || 'none'} {size} hoverText={hoverText || ''} {wiggle} />
	{/if}
</button>

<!-- svelte-ignore css_unused_selector-->
<style>
	button:disabled :global {
		cursor: default;
		--tw-border-opacity: 1;
		border-color: rgb(227 227 227 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(227 227 227 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

button:disabled :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(56 56 56 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
		fill: #4e4e4e;
		--tw-text-opacity: 1;
		color: rgb(125 125 125 / var(--tw-text-opacity, 1));
}

	.primary {
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.primary.primary-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(44 58 73 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(44 58 73 / var(--tw-bg-opacity, 1));
}

	.primary.primary-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
		fill: #2c3a49;
}

	.primary.secondary-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
}

	.primary.secondary-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.primary.error-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(130 7 31 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(130 7 31 / var(--tw-bg-opacity, 1));
}

	.primary.error-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(255 152 0 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(255 152 0 / var(--tw-bg-opacity, 1));
}

	.primary.warning-color {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(45 218 255 / var(--tw-bg-opacity, 1));
}

	.primary.primary-color:hover,
	.primary.primary-color.active :global {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
}

	.primary.primary-color:hover:where(.dark, .dark *),
	.primary.primary-color.active :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
}

	.primary.secondary-color:hover,
	.primary.secondary-color.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(45 218 255 / var(--tw-bg-opacity, 1));
}

	.primary.secondary-color:hover:where(.dark, .dark *),
	.primary.secondary-color.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.primary.secondary-color:hover:hover:where(.dark, .dark *),
	.primary.secondary-color.active :global:hover:where(.dark, .dark *) {
		fill: #383838;
}

	.primary.error-color:hover,
	.primary.error-color.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
		fill: #82071f;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.primary.error-color:hover:where(.dark, .dark *),
	.primary.error-color.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
		fill: #ff9800;
}

	.primary.warning-color:hover,
	.primary.warning-color.active {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(45 218 255 / var(--tw-bg-opacity, 1));
}

	.primary:disabled :global {
		--tw-bg-opacity: 1;
		background-color: rgb(227 227 227 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.primary:disabled :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
		fill: #4e4e4e;
		--tw-text-opacity: 1;
		color: rgb(101 101 101 / var(--tw-text-opacity, 1));
}

	.secondary :global {
		--tw-bg-opacity: 1;
		background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
}

	.secondary :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}

	.secondary.primary-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(44 58 73 / var(--tw-border-opacity, 1));
		fill: #2c3a49;
}

	.secondary.primary-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		fill: #fdfdfd;
}

	.secondary.primary-color:hover:not(.borderless),
	.secondary.primary-color.active:not(.borderless) :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary.primary-color:hover:not(.borderless):where(.dark, .dark *),
	.secondary.primary-color.active:not(.borderless) :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.secondary.primary-color:hover,
	.secondary.primary-color.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
		fill: #1789c0;
}

	.secondary.primary-color:hover:where(.dark, .dark *),
	.secondary.primary-color.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.secondary.secondary-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		fill: #1789c0;
		--tw-text-opacity: 1;
		color: rgb(23 137 192 / var(--tw-text-opacity, 1));
}

	.secondary.secondary-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		fill: #92bcdc;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary.secondary-color:hover,
	.secondary.secondary-color.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary.secondary-color:hover:where(.dark, .dark *),
	.secondary.secondary-color.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.secondary.error-color :global {
		--tw-border-opacity: 1;
		border-color: rgb(130 7 31 / var(--tw-border-opacity, 1));
		fill: #82071f;
		--tw-text-opacity: 1;
		color: rgb(130 7 31 / var(--tw-text-opacity, 1));
}

	.secondary.error-color :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(255 152 0 / var(--tw-border-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(255 152 0 / var(--tw-text-opacity, 1));
}

	.secondary.warning-color {
		--tw-border-opacity: 1;
		border-color: rgb(45 218 255 / var(--tw-border-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary.error-color:hover,
	.secondary.error-color.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(130 7 31 / var(--tw-bg-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary.error-color:hover:where(.dark, .dark *),
	.secondary.error-color.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(255 152 0 / var(--tw-bg-opacity, 1));
}

	.secondary.warning-color:hover,
	.secondary.warning-color.active {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		fill: #fdfdfd;
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.borderless {
		border: none !important;
		background-color: transparent !important;
	}

	.borderless:hover,
	.borderless.active {
		color: var(--primary-normal) !important;
	}
</style>
